import { assertDefined } from '@dynamic-labs-sdk/client/core';
import type { EvmWalletAccount } from '@dynamic-labs-sdk/evm';

import type { BatchCall } from '../BatchCall.types';
import { createKernelClientForWalletAccount } from '../createKernelClientForWalletAccount';
import type { KernelClient } from '../KernelClient.types';
import { prepareUserOperationFromCalls } from '../utils/prepareUserOperationFromCalls';

type CanSponsorUserOperationBaseParams = {
  calls: BatchCall[];
};

type CanSponsorUserOperationWithWalletParams =
  CanSponsorUserOperationBaseParams & {
    kernelClient?: never;
    walletAccount: EvmWalletAccount;
  };

type CanSponsorUserOperationWithClientParams =
  CanSponsorUserOperationBaseParams & {
    kernelClient: KernelClient;
    walletAccount?: never;
  };

export type CanSponsorUserOperationParams =
  | CanSponsorUserOperationWithWalletParams
  | CanSponsorUserOperationWithClientParams;

/**
 * Checks if a user operation can be sponsored by the paymaster
 * Handles both single transactions and batch transactions
 *
 * @param params.calls - Array of calls (single call or multiple calls)
 * @param params.kernelClient - Optional kernel client with sponsorship configured
 * @param params.walletAccount - The wallet account to use (required if kernelClient not provided)
 * @returns True if the user operation can be sponsored, false otherwise
 */
export const canSponsorUserOperation = async ({
  walletAccount,
  calls,
  kernelClient,
}: CanSponsorUserOperationParams): Promise<boolean> => {
  let kernelClientToUse = kernelClient;

  if (!kernelClientToUse) {
    assertDefined(
      walletAccount,
      'Please provide either a wallet account or a kernel client in the parameters'
    );

    kernelClientToUse = await createKernelClientForWalletAccount({
      smartWalletAccount: walletAccount,
    });
  }

  try {
    const sponsorResult = await prepareUserOperationFromCalls({
      calls,
      kernelClient: kernelClientToUse,
    });

    const hasPaymaster = sponsorResult.paymasterAndData !== '0x';

    return hasPaymaster;
  } catch {
    return false;
  }
};
